import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { EmployeeTag } from '../types/employeeTags';

export type { EmployeeTag };

interface EmployeeTagsState {
  tags: EmployeeTag[];
  isLoading: boolean;
  error: string | null;
}

const initialState: EmployeeTagsState = {
  tags: [],
  isLoading: false,
  error: null,
};

function computeFullTagTitles(tags: EmployeeTag[]): EmployeeTag[] {
  // DNA structure from backend: [0, rootTagId, ...ancestors, selfTagId]
  // depthFromRoot = dna.length - 2  (root=0, 1st-level child=1, 2nd-level=2, ...)
  const tagMap = new Map<number, EmployeeTag>(tags.map((t) => [t.tagId, t]));

  // Process shallowest first so parents are resolved before children
  const sorted = [...tags].sort((a, b) => a.dna.length - b.dna.length);

  const fullTitleMap = new Map<number, string>();

  for (const tag of sorted) {
    const depthFromRoot = tag.dna.length - 2;

    if (depthFromRoot <= 0) {
      // Root/category tag
      fullTitleMap.set(tag.tagId, tag.tagTitle);
    } else {
      const rootId = tag.dna[1]; // dna[1] is always the root/category tagId
      const concatTitles = tagMap.get(rootId)?.concatTitles ?? false;

      if (!concatTitles || depthFromRoot === 1) {
        // concat=false, or 1st-level child → title unchanged
        fullTitleMap.set(tag.tagId, tag.tagTitle);
      } else {
        // 2nd+ level child with concat=true; tag itself is last in dna, parent is second-to-last
        const parentId = tag.dna[tag.dna.length - 2];
        const parentFull = fullTitleMap.get(parentId) ?? tagMap.get(parentId)?.tagTitle ?? '';
        fullTitleMap.set(tag.tagId, `${parentFull} ${tag.tagTitle}`);
      }
    }
  }

  return tags.map((tag) => ({
    ...tag,
    fullTagTitle: fullTitleMap.get(tag.tagId) ?? tag.tagTitle,
  }));
}

const employeeTagsSlice = createSlice({
  name: 'employeeTags',
  initialState,
  reducers: {
    setEmployeeTags(state, action: PayloadAction<EmployeeTag[]>) {
      state.tags = computeFullTagTitles(action.payload);
    },
    addEmployeeTag(state, action: PayloadAction<EmployeeTag>) {
      state.tags.push(action.payload);
    },
    replaceEmployeeTag(state, action: PayloadAction<EmployeeTag>) {
      const idx = state.tags.findIndex((t) => t.tagId === action.payload.tagId);
      if (idx !== -1) {
        state.tags[idx] = action.payload;
      } else {
        state.tags.push(action.payload);
      }
    },
    removeEmployeeTag(state, action: PayloadAction<number>) {
      // Remove tag and all its descendants (any tag whose dna contains this tagId)
      state.tags = state.tags.filter(
        (t) => t.tagId !== action.payload && !t.dna.includes(action.payload)
      );
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const {
  setEmployeeTags,
  addEmployeeTag,
  replaceEmployeeTag,
  removeEmployeeTag,
  setLoading,
  setError,
} = employeeTagsSlice.actions;

export const fetchEmployeeTags =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.listEmployeeTags();
      dispatch(setEmployeeTags(response.data ?? []));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load employee tags'));
    } finally {
      dispatch(setLoading(false));
    }
  };

export default employeeTagsSlice.reducer;
